import { isRef, nextTick } from 'vue';
import type { I18n } from 'vue-i18n';
import type { Router } from 'vue-router';

export const SUPPORTED_LOCALES = [
  'en',
  'ar',
  'es-mx',
  'fr',
  'hi',
  'pl',
  'pt-br',
  'ru',
  'uk',
  'ms',
  'zh',
  'pa'
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Matches GlobalSEO default `data-lang-parameter` when unset. */
export const JUNO_LANG_QUERY = 'lang';

/** Persisted when the user picks a language (aligned with `public/language-bootstrap.js`). */
export const USER_SELECTED_LANGUAGE_KEY = 'user-selected-language';

function normalizeToSupportedLocale(raw: string | null | undefined): SupportedLocale | null {
  if (!raw) return null;
  const lower = raw.trim().toLowerCase();
  if ((SUPPORTED_LOCALES as readonly string[]).includes(lower)) return lower as SupportedLocale;
  const prefix = lower.split('-')[0];
  const match = SUPPORTED_LOCALES.find(l => l.split('-')[0] === prefix);
  return match ?? null;
}

function detectBrowserLocale(): SupportedLocale | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages?.length ? [...navigator.languages] : [navigator.language];
  for (const lang of langs) {
    const resolved = normalizeToSupportedLocale(lang);
    if (resolved) return resolved;
  }
  return null;
}

function setVueI18nLocale(i18n: I18n, code: SupportedLocale): void {
  const g = i18n.global as unknown as { locale: unknown };
  if (isRef(g.locale)) {
    (g.locale as { value: string }).value = code;
  } else {
    (g as { locale: string }).locale = code;
  }
}

function persistUserLanguage(code: SupportedLocale): void {
  try {
    localStorage.setItem(USER_SELECTED_LANGUAGE_KEY, code);
    localStorage.setItem('language', code);
  } catch {
    console.error('Error persisting user language:', code);
  }
}

function getGlobalSeoScriptTag(): HTMLScriptElement | null {
  if (window.translationScriptTag) {
    return window.translationScriptTag;
  }

  const el = document.querySelector<HTMLScriptElement>(
    'script[data-globalseo-key], script[data-weploy-key]'
  );
  if (el) {
    window.translationScriptTag = el;
  }
  return el;
}

/** Align runtime language state GlobalSEO reads during translation cycles. */
export function applyGlobalSeoLanguageState(resolved: SupportedLocale): void {
  window.paramsLang = resolved;
  window.globalseoActiveLang = resolved;
}

/**
 * Re-runs GlobalSEO after an in-app locale change. Initial page load is handled by
 * `translate.js`; vue-i18n updates alone do not re-translate dynamic DOM (e.g. custom fields).
 */
export async function syncGlobalSeoLocale(resolved: SupportedLocale): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  applyGlobalSeoLanguageState(resolved);

  const utils = window.globalseoUtils;
  if (!utils?.getTranslations || !utils.extractOptionsFromScript) {
    return;
  }

  const scriptTag = getGlobalSeoScriptTag();
  if (!scriptTag) {
    return;
  }

  const options = utils.extractOptionsFromScript(window, { activeLanguage: resolved });
  const apiKey =
    (typeof options.apiKey === 'string' && options.apiKey) ||
    scriptTag.getAttribute('data-globalseo-key') ||
    scriptTag.getAttribute('data-weploy-key') ||
    import.meta.env.VITE_GLOBALSEO_KEY;

  if (!apiKey) {
    return;
  }

  const opts = {
    ...options,
    activeLanguage: resolved,
    paramsLang: resolved,
    createSelector: false
  };
  utils.setOptions?.(window, apiKey, opts);
  await utils.getTranslations(window, apiKey, opts);
}

/**
 * Applies locale from URL → persisted choice → browser → `en`, then ensures `?lang=` is set
 * for GlobalSEO `searchParams` mode.
 */
export async function applyInitialJunoLocale(router: Router, i18n: I18n): Promise<void> {
  const rawLang = router.currentRoute.value.query[JUNO_LANG_QUERY];
  const fromQuery = normalizeToSupportedLocale(Array.isArray(rawLang) ? rawLang[0] : rawLang);
  let fromStorage: SupportedLocale | null = null;
  try {
    fromStorage = normalizeToSupportedLocale(localStorage.getItem(USER_SELECTED_LANGUAGE_KEY));
  } catch {
    fromStorage = null;
  }
  const resolved = fromQuery ?? fromStorage ?? detectBrowserLocale() ?? 'en';

  setVueI18nLocale(i18n, resolved);
  document.documentElement.setAttribute('lang', resolved);

  const currentLang = router.currentRoute.value.query[JUNO_LANG_QUERY];
  const currentStr = Array.isArray(currentLang) ? currentLang[0] : currentLang;
  if (String(currentStr || '').toLowerCase() !== resolved) {
    await router.replace({
      query: { ...router.currentRoute.value.query, [JUNO_LANG_QUERY]: resolved }
    });
  }
}

export async function setJunoLocale(
  router: Router,
  i18n: I18n,
  code: SupportedLocale
): Promise<void> {
  persistUserLanguage(code);
  applyGlobalSeoLanguageState(code);
  setVueI18nLocale(i18n, code);
  document.documentElement.setAttribute('lang', code);
  await router.replace({
    query: { ...router.currentRoute.value.query, [JUNO_LANG_QUERY]: code }
  });
  // Remount locale-keyed custom fields back to API source strings before re-translating.
  await nextTick();
  await syncGlobalSeoLocale(code);
}

export function getLanguageOptions(titleFor: (code: SupportedLocale) => string) {
  return SUPPORTED_LOCALES.map(value => ({ value, title: titleFor(value) }));
}
